import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, MapPin, Globe2, ShieldCheck, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

// Read-only public view of another user's profile.
// Reached from Chat header ("tap on name") and Discover overlay.
export default function UserView() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [target, setTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoIndex, setPhotoIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api(`/users/${userId}`);
        if (cancelled) return;
        setTarget(data.user || null);
      } catch (err) {
        if (!cancelled) toast.error(err.message || 'Could not load profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="user-view-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#C8553D]" />
      </div>
    );
  }

  if (!target) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center" data-testid="user-view-empty">
        <p className="text-[#6D6875]">This profile is no longer available.</p>
        <button
          onClick={() => navigate(-1)}
          data-testid="user-view-go-back"
          className="mt-6 px-6 py-2 rounded-full bg-[#C8553D] text-white font-medium hover:bg-[#9E3A26]"
        >
          Go back
        </button>
      </div>
    );
  }

  const photos = target.photos || [];
  const currentPhoto = photos[photoIndex];
  const languages = target.languages || [];
  const intents = target.relationship_intents?.length
    ? target.relationship_intents
    : target.relationship_intent
    ? [target.relationship_intent]
    : [];

  return (
    <div className="max-w-2xl mx-auto px-4 py-6" data-testid="user-view-page">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        data-testid="user-view-back-button"
        className="flex items-center gap-2 text-[#6D6875] hover:text-[#C8553D] mb-4"
      >
        <ArrowLeft className="w-5 h-5" /> Back
      </button>

      {/* Photo gallery */}
      <div className="relative aspect-[3/4] w-full bg-[#FFE5DD] rounded-3xl overflow-hidden mb-4">
        {currentPhoto ? (
          <img
            src={currentPhoto}
            alt={target.name}
            className="w-full h-full object-cover"
            data-testid="user-view-photo"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center text-7xl font-bold text-[#C8553D]"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="user-view-initial"
          >
            {target.name?.charAt(0) || '?'}
          </div>
        )}

        {photos.length > 1 && (
          <div className="absolute top-3 left-3 right-3 flex gap-1">
            {photos.map((_, i) => (
              <div
                key={i}
                className={`flex-1 h-1 rounded-full ${i === photoIndex ? 'bg-white' : 'bg-white/40'}`}
              />
            ))}
          </div>
        )}

        {photos.length > 1 && (
          <>
            <button
              onClick={() => setPhotoIndex((i) => (i - 1 + photos.length) % photos.length)}
              data-testid="user-view-prev-photo"
              className="absolute left-0 top-0 bottom-0 w-1/3"
              aria-label="Previous photo"
            />
            <button
              onClick={() => setPhotoIndex((i) => (i + 1) % photos.length)}
              data-testid="user-view-next-photo"
              className="absolute right-0 top-0 bottom-0 w-1/3"
              aria-label="Next photo"
            />
          </>
        )}
      </div>

      {/* Name + age */}
      <div className="flex items-end justify-between mb-2">
        <h1
          className="text-3xl font-bold text-[#2B2D42]"
          style={{ fontFamily: 'Playfair Display, serif' }}
          data-testid="user-view-name"
        >
          {target.name}
          {target.age ? <span className="font-normal text-[#6D6875]">, {target.age}</span> : null}
        </h1>
        {target.verification_status === 'verified' && (
          <span className="inline-flex items-center gap-1 text-xs text-[#2A9D8F] font-medium" data-testid="user-view-verified">
            <ShieldCheck className="w-4 h-4" /> Verified
          </span>
        )}
      </div>

      {/* Location + heritage */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#6D6875] mb-4">
        {target.city && (
          <span className="inline-flex items-center gap-1" data-testid="user-view-city">
            <MapPin className="w-4 h-4" /> {target.city}
          </span>
        )}
        {target.country_of_origin && (
          <span className="inline-flex items-center gap-1" data-testid="user-view-country">
            <Globe2 className="w-4 h-4" /> {target.country_of_origin}
          </span>
        )}
      </div>

      {/* Bio */}
      {target.bio && (
        <div className="bg-[#FFFCF9] border border-[#F4F1DE] rounded-2xl p-4 mb-4" data-testid="user-view-bio">
          <p className="text-[#2B2D42] whitespace-pre-line">{target.bio}</p>
        </div>
      )}

      {/* Chips: languages, religion, tribe, intents */}
      <div className="flex flex-wrap gap-2 mb-6">
        {languages.slice(0, 6).map((l) => (
          <span key={l} data-testid={`user-view-language-${l}`} className="text-xs bg-[#FFE5DD] text-[#C8553D] px-3 py-1 rounded-full">
            {l}
          </span>
        ))}
        {target.tribe && (
          <span data-testid="user-view-tribe" className="text-xs bg-[#F4F1DE] text-[#2B2D42] px-3 py-1 rounded-full">
            {target.tribe}
          </span>
        )}
        {target.religion && (
          <span data-testid="user-view-religion" className="text-xs bg-[#F4F1DE] text-[#2B2D42] px-3 py-1 rounded-full">
            {target.religion}
          </span>
        )}
        {intents.map((intent) => (
          <span
            key={intent}
            data-testid={`user-view-intent-${intent}`}
            className="text-xs bg-[#C8553D] text-white px-3 py-1 rounded-full inline-flex items-center gap-1"
          >
            <Sparkles className="w-3 h-3" /> {intent}
          </span>
        ))}
      </div>
    </div>
  );
}
